const SMART_BANNER_ID = "smart-banner";
const SMART_BANNER_SPACER_ATTRIBUTE = "data-afterpay-smart-banner-spacer";

type SmartBannerCallback = () => void;

const getSmartBanners = (): HTMLElement[] =>
    Array.from(document.querySelectorAll<HTMLElement>(`#${SMART_BANNER_ID}`));

const isSmartBannerSpacer = (element: Element | null): element is HTMLElement => {
    if (!(element instanceof HTMLElement)) {
        return false;
    }

    return (
        element.tagName === "DIV" &&
        !element.id &&
        !element.className &&
        element.childElementCount === 0 &&
        element.textContent?.trim() === "" &&
        element.style.height !== ""
    );
};

const markSmartBannerSpacer = (element: Element | null): HTMLElement | null => {
    if (!isSmartBannerSpacer(element)) {
        return null;
    }

    element.setAttribute(SMART_BANNER_SPACER_ATTRIBUTE, "true");

    return element;
};

const removeMarkedOrphanSpacers = (primarySpacer?: HTMLElement | null): void => {
    document
        .querySelectorAll<HTMLElement>(`[${SMART_BANNER_SPACER_ATTRIBUTE}="true"]`)
        .forEach((spacer) => {
            if (spacer !== primarySpacer || spacer.previousElementSibling?.id !== SMART_BANNER_ID) {
                spacer.remove();
            }
        });
};

export const normalizeSmartBanner = (): void => {
    const [primaryBanner, ...duplicateBanners] = getSmartBanners();

    if (!primaryBanner) {
        removeMarkedOrphanSpacers();
        return;
    }

    const primarySpacer = markSmartBannerSpacer(primaryBanner.nextElementSibling);

    duplicateBanners.forEach((banner) => {
        markSmartBannerSpacer(banner.nextElementSibling)?.remove();
        banner.remove();
    });

    removeMarkedOrphanSpacers(primarySpacer);
};

export const hideSmartBanner = (): void => {
    getSmartBanners().forEach((banner) => {
        markSmartBannerSpacer(banner.nextElementSibling)?.remove();
        banner.remove();
    });
    removeMarkedOrphanSpacers();
};

export const observeSmartBanner = (callback: SmartBannerCallback): (() => void) => {
    callback();

    if (typeof MutationObserver === "undefined") {
        return () => undefined;
    }

    const observer = new MutationObserver(callback);
    observer.observe(document.body, { childList: true });

    return () => observer.disconnect();
};
